import React, { useState, useEffect, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import useMediaQuery from '../utils/useMediaQuery';
import useDialog from '../utils/useDialog';

import { API } from '../config';

function resolveMediaUrl(u) {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  return `${API}${u}`;
}

/**
 * LandmarkPanel — kid-friendly detail panel for world landmarks.
 * Mobile: bottom sheet with sticky header.
 * Desktop: side panel with sticky close button.
 */
export default function LandmarkPanel({ name, onClose }) {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(false);
  const [imgState, setImgState] = useState('loading'); // loading | ready | error
  const openedAtRef = React.useRef(Date.now());

  useEffect(() => {
    setData(null);
    setLoading(true);
    setError(false);
    setImgState('loading');

    const controller = new AbortController();
    const timeout    = setTimeout(() => controller.abort(), 30_000);

    fetch(`${API}/api/landmark/${encodeURIComponent(name)}`, { signal: controller.signal })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d  => setData(d))
      .catch(() => setError(true))
      .finally(() => { clearTimeout(timeout); setLoading(false); });

    return () => { controller.abort(); clearTimeout(timeout); };
  }, [name]);

  const handleClose = useCallback(() => onClose(), [onClose]);
  const dialogRef = useDialog({ onClose: handleClose });
  const reduced = useReducedMotion();
  const handleBackdropClick = useCallback(() => {
    // Mobile Safari/Chrome may synthesize a click after the marker pointerup
    // that opened this sheet. Ignore that opening tap so it cannot close the
    // newly mounted backdrop immediately.
    if (Date.now() - openedAtRef.current < 450) return;
    handleClose();
  }, [handleClose]);

  // ── Mobile: bottom-sheet with overlay ──────────────────────────
  if (isMobile) {
    return (
      <>
        {/* Dimmed backdrop */}
        <motion.div
          style={styles.backdrop}
          onClick={handleBackdropClick}
          aria-hidden="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        />

        {/* Bottom sheet */}
        <motion.div
          className="orboot-scroll"
          style={styles.sheet}
          ref={dialogRef}
          tabIndex={-1}
          aria-modal="true"
          role="dialog"
          aria-label={`About ${name}`}
          initial={reduced ? { opacity: 0 } : { y: '100%' }}
          animate={reduced ? { opacity: 1 } : { y: 0 }}
          exit={reduced ? { opacity: 0 } : { y: '100%' }}
          transition={{ type: 'spring', stiffness: 260, damping: 30 }}
        >
          {/* Sticky drag handle + header */}
          <div style={styles.sheetHeader}>
            <div style={styles.sheetHandle} aria-hidden="true" />
            <p style={styles.sheetTitle}>{name}</p>
            <button
              onClick={handleClose}
              style={styles.sheetClose}
              aria-label="Close landmark"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scrollable body */}
          <div style={styles.sheetBody}>
            {loading && <LoadingBox />}
            {error && !loading && <ErrorBox onClose={handleClose} />}
            {data && !loading && <Content name={name} data={data} imgState={imgState} setImgState={setImgState} isMobile />}
          </div>
        </motion.div>
      </>
    );
  }

  // ── Desktop: side panel ─────────────────────────────────────────
  return (
    <motion.div
      className="orboot-scroll"
      style={styles.panel}
      ref={dialogRef}
      tabIndex={-1}
      aria-modal="true"
      role="dialog"
      aria-label={`About ${name}`}
      initial={reduced ? { opacity: 0 } : { opacity: 0, x: 40 }}
      animate={reduced ? { opacity: 1 } : { opacity: 1, x: 0 }}
      exit={reduced ? { opacity: 0 } : { opacity: 0, x: 40 }}
      transition={{ type: 'spring', stiffness: 240, damping: 28 }}
    >
      {/* Sticky close button */}
      <div style={styles.panelHeader}>
        <button
          onClick={handleClose}
          style={styles.closeBtn}
          aria-label="Close and go back to globe"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      <div style={styles.panelBody}>
        {loading && <LoadingBox />}
        {error && !loading && <ErrorBox onClose={handleClose} />}
        {data && !loading && <Content name={name} data={data} imgState={imgState} setImgState={setImgState} />}
      </div>
    </motion.div>
  );
}

// ── Shared content ────────────────────────────────────────────────
function Content({ name, data, imgState, setImgState, isMobile }) {
  return (
    <>
      <div style={{ ...styles.imageWrap, ...(isMobile ? styles.imageWrapMobile : {}) }}>
        {imgState !== 'ready' && (
          <div style={{ ...styles.imagePlaceholder, ...(imgState === 'error' ? styles.imagePlaceholderError : {}) }}>
            <div style={styles.placeholderEmoji} aria-hidden="true">
              {imgState === 'error' ? '🌍' : '🎨'}
            </div>
            <div style={styles.placeholderText}>
              {imgState === 'error' ? 'Picture is taking a nap' : 'Painting your card…'}
            </div>
          </div>
        )}
        {data.imageUrl && imgState !== 'error' && (
          <img
            src={resolveMediaUrl(data.imageUrl)}
            alt={`Illustrated ${name}`}
            style={{ ...styles.image, opacity: imgState === 'ready' ? 1 : 0, position: imgState === 'ready' ? 'static' : 'absolute' }}
            onLoad={() => setImgState('ready')}
            onError={() => setImgState('error')}
          />
        )}
      </div>

      {/* Title — only shown on desktop; mobile has sticky header */}
      {!isMobile && (
        <h1 style={styles.title}>{name}</h1>
      )}

      <div style={styles.tags}>
        {data.content?.location && <span style={styles.tag}>📍 {data.content.location}</span>}
        {data.content?.builtIn && data.content.builtIn !== 'unknown' && <span style={styles.tag}>🏗️ {data.content.builtIn}</span>}
        {data.content?.height && data.content.height !== 'N/A' && <span style={{ ...styles.tag, ...styles.tagGreen }}>📏 {data.content.height}</span>}
      </div>

      {data.content?.description && (
        <Section icon="🏛️" label="What is it?">
          <p style={{ ...styles.body, ...(isMobile ? styles.bodyMobile : {}) }}>{data.content.description}</p>
        </Section>
      )}

      {data.content?.funFact && (
        <div style={styles.funFactBox}>
          <p style={styles.funFactLabel}>
            <span style={styles.funFactStar} aria-hidden="true">✨</span>
            Did you know?
          </p>
          <p style={{ ...styles.funFactBody, ...(isMobile ? styles.bodyMobile : {}) }}>{data.content.funFact}</p>
        </div>
      )}

      <p style={styles.aiDisclosure}>Story by a friendly robot 🤖</p>
    </>
  );
}

function LoadingBox() {
  return (
    <div style={styles.loadingBox}>
      <div style={styles.loadingEmoji} aria-hidden="true">🎨</div>
      <p style={styles.loadingTitle}>Painting your card…</p>
      <p style={styles.loadingHint}>Our friendly robot is drawing this landmark!</p>
      <div style={styles.dots} aria-hidden="true">
        <span style={{ ...styles.dot, animationDelay: '0s' }} />
        <span style={{ ...styles.dot, animationDelay: '0.2s' }} />
        <span style={{ ...styles.dot, animationDelay: '0.4s' }} />
      </div>
    </div>
  );
}

function ErrorBox({ onClose }) {
  return (
    <div style={styles.loadingBox}>
      <div style={styles.loadingEmoji} aria-hidden="true">🦒</div>
      <p style={styles.loadingTitle}>Hmm, that one's hiding.</p>
      <p style={styles.loadingHint}>Try tapping another landmark!</p>
      <button onClick={onClose} style={styles.errorBtn}>Back to globe</button>
    </div>
  );
}

function Section({ icon, label, children }) {
  return (
    <div style={styles.section}>
      <p style={styles.sectionLabel}>
        <span style={styles.sectionIcon} aria-hidden="true">{icon}</span>
        {label}
      </p>
      {children}
    </div>
  );
}

const styles = {
  // ── Desktop side panel ────────────────────────────────────────
  panel: {
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    width: '100%',
    maxWidth: 440,
    background: '#ffffff',
    color: '#0f172a',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '-24px 0 60px rgba(2, 6, 23, 0.45)',
    animation: 'orboot-pop-in 320ms cubic-bezier(0.16, 1, 0.3, 1)',
    boxSizing: 'border-box',
    borderLeft: '1px solid rgba(15, 23, 42, 0.06)',
  },
  panelHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    background: '#ffffff',
    padding: '16px 20px 10px',
    borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
    display: 'flex',
    alignItems: 'center',
  },
  panelBody: {
    padding: '16px 24px calc(env(safe-area-inset-bottom, 0px) + 80px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    flex: 1,
  },
  closeBtn: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    background: '#ffffff',
    color: '#0f172a',
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'transform 150ms var(--ease-out), box-shadow 150ms var(--ease-out)',
  },

  // ── Mobile bottom sheet ───────────────────────────────────────
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(2, 6, 23, 0.48)',
    zIndex: 200,
    animation: 'orboot-fade-in 200ms ease',
  },
  sheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 201,
    background: '#ffffff',
    borderRadius: '20px 20px 0 0',
    maxHeight: '88vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 -8px 40px rgba(2, 6, 23, 0.28)',
    animation: 'orboot-slide-up 320ms cubic-bezier(0.16, 1, 0.3, 1)',
    boxSizing: 'border-box',
    overflowY: 'hidden',
  },
  sheetHeader: {
    flexShrink: 0,
    padding: '12px 16px 10px',
    borderBottom: '1px solid rgba(15, 23, 42, 0.07)',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gridTemplateRows: 'auto auto',
    alignItems: 'center',
    gap: '4px 8px',
    background: '#ffffff',
    borderRadius: '20px 20px 0 0',
  },
  sheetHandle: {
    gridColumn: '1 / -1',
    width: 36,
    height: 4,
    borderRadius: 99,
    background: 'rgba(15, 23, 42, 0.12)',
    margin: '0 auto 6px',
  },
  sheetTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: '#0f172a',
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sheetClose: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: '1px solid rgba(15, 23, 42, 0.10)',
    background: '#f8fafc',
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'background 150ms var(--ease-out)',
  },
  sheetBody: {
    overflowY: 'auto',
    flex: 1,
    padding: '14px 16px calc(env(safe-area-inset-bottom, 0px) + 24px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },

  // ── Loading / error ───────────────────────────────────────────
  loadingBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '36px 0',
    gap: 10,
  },
  loadingEmoji: {
    fontSize: 52,
    display: 'inline-block',
    animation: 'orboot-bob 1.8s ease-in-out infinite',
  },
  loadingTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
    letterSpacing: '-0.01em',
  },
  loadingHint: {
    fontSize: 14,
    color: '#64748b',
    margin: 0,
    lineHeight: 1.5,
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: 6,
    marginTop: 8,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#fbbf24',
    display: 'inline-block',
    animation: 'orboot-bob 1s ease-in-out infinite',
  },
  errorBtn: {
    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    color: '#0f172a',
    border: 'none',
    borderRadius: 999,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(251,191,36,0.32)',
    marginTop: 12,
    letterSpacing: '-0.005em',
  },

  // ── Image ─────────────────────────────────────────────────────
  imageWrap: {
    position: 'relative',
    width: '100%',
    aspectRatio: '1 / 1',
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12), inset 0 0 0 1px rgba(15, 23, 42, 0.04)',
    background: '#f1f5f9',
    flexShrink: 0,
    margin: '4px 0 2px',
  },
  imageWrapMobile: {
    aspectRatio: '16 / 9',
    borderRadius: 14,
  },
  image: {
    width: '100%',
    height: '100%',
    borderRadius: 20,
    objectFit: 'cover',
    transition: 'opacity 320ms var(--ease-out)',
    display: 'block',
  },
  imagePlaceholder: {
    position: 'absolute',
    inset: 0,
    borderRadius: 20,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    color: '#64748b',
    fontSize: 14,
    fontWeight: 600,
    background: 'linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%)',
    backgroundSize: '800px 100%',
    animation: 'orboot-shimmer 1.8s linear infinite',
  },
  imagePlaceholderError: {
    background: '#fef2f2',
    color: '#991b1b',
    animation: 'none',
  },
  placeholderEmoji: { fontSize: 48 },
  placeholderText:  { fontSize: 14 },

  // ── Text ──────────────────────────────────────────────────────
  title: {
    fontSize: 30,
    fontWeight: 700,
    margin: '4px 0 0',
    color: '#0f172a',
    letterSpacing: '-0.025em',
    lineHeight: 1.1,
  },

  tags: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    margin: '2px 0',
  },
  tag: {
    background: '#f1f5f9',
    color: '#334155',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 12.5,
    fontWeight: 600,
    letterSpacing: '-0.005em',
  },
  tagGreen: {
    background: '#dcfce7',
    color: '#166534',
  },

  section: { marginTop: 4 },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: '#64748b',
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    margin: '0 0 6px',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  sectionIcon: { fontSize: 14 },
  body: {
    fontSize: 16,
    lineHeight: 1.6,
    color: '#334155',
    margin: 0,
    fontWeight: 400,
  },
  bodyMobile: {
    fontSize: 15,
    lineHeight: 1.55,
  },

  funFactBox: {
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    border: '1px solid rgba(251, 191, 36, 0.30)',
    borderRadius: 16,
    padding: 18,
    boxShadow: '0 4px 14px rgba(251, 191, 36, 0.12)',
  },
  funFactLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: '#78350f',
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    margin: '0 0 6px',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  funFactStar: {
    fontSize: 14,
    display: 'inline-block',
    animation: 'orboot-wiggle 1.8s ease-in-out infinite',
  },
  funFactBody: {
    fontSize: 16,
    lineHeight: 1.55,
    color: '#451a03',
    margin: 0,
    fontWeight: 500,
  },

  aiDisclosure: {
    fontSize: 11.5,
    color: '#94a3b8',
    margin: '4px 0 0 2px',
    fontWeight: 500,
  },
};
